import { ref, computed } from '@vue/composition-api'
import store from '@store'

// Who is looking at the Invoicing tab. Superusers reach it from the
// Invoicing page; TPA and Underwriter company admins from the Billing tab
// on their own Manage Users page (CompanyBillingInvoicing.vue). The proxy
// enforces every one of these rules server-side too — this only decides
// what to show.
export function useInvoicingRole() {
  const user = computed(() => store.getters.user || {})
  const companyRole = computed(() => {
    const company = user.value.company
    return (company && company.role && company.role.role) || null
  })

  const isSuperuser = computed(() => !!user.value.is_superuser)
  const isTpa = computed(() => !isSuperuser.value && companyRole.value === 'TPA')
  const isUnderwriter = computed(() => !isSuperuser.value && companyRole.value === 'Underwriter')

  return { isSuperuser, isTpa, isUnderwriter }
}

// The message to show for a failed request: the server's own `error`
// text when it sent one (validation failures, scope refusals), else the
// caller's fallback. Every response is wrapped as {status, data: <body>}
// by the API renderer (api/utils.py APIRenderer), errors included. Blob
// responses (downloads) carry that JSON as a Blob, so it's read first.
export async function requestErrorMessage(error, fallback) {
  const data = error && error.response && error.response.data
  if (!data) return fallback
  try {
    const envelope = data instanceof Blob ? JSON.parse(await data.text()) : data
    const body = envelope && envelope.data !== undefined ? envelope.data : envelope
    return (body && body.error) || fallback
  } catch (e) {
    return fallback
  }
}

// The Lincoln body inside the API renderer's {status, data} envelope —
// e.g. {status, data: rows, count} for a list.
export const responseBody = (resp) => (resp && resp.data && resp.data.data) || {}

/**
 * Row selection for one Invoicing-tab table (LIQ-2071).
 *
 * Selected rows are kept as id -> the few fields that decide which
 * toolbar actions apply to them, so a selection can span pages the table
 * hasn't loaded. "Select all" fetches those fields for every row matching
 * the current search/filters from the server (fetchAllMatching), so it
 * really does select every page.
 *
 * Kept on page change and sort change; the owning table calls clear() when
 * the search or filters change, after a bulk action completes, and it is
 * dropped on refresh/navigation anyway since nothing persists it.
 *
 * @param {Object} options
 * @param {(row) => string|number} options.idOf  row -> selection key
 * @param {(row) => Object} options.metaOf  row -> fields kept per selected row
 * @param {() => Promise<Array>} options.fetchAllMatching  every matching row's meta
 */
export function useInvoicingSelection({ idOf, metaOf, fetchAllMatching }) {
  const selected = ref({})
  const total = ref(0)
  const selectingAll = ref(false)

  // Bumped by clear() so a "select all" fetch still in flight when the
  // filters change can't land its now-stale result afterwards.
  let generation = 0

  const selectedCount = computed(() => Object.keys(selected.value).length)
  const selectedIds = computed(() => Object.keys(selected.value))
  const selectedRows = computed(() => Object.values(selected.value))
  const allSelected = computed(() => total.value > 0 && selectedCount.value >= total.value)

  const headerState = computed(() => {
    if (!selectedCount.value) return 'none'
    return allSelected.value ? 'all' : 'some'
  })

  const isSelected = (row) => !!selected.value[idOf(row)]

  // Always reassigned rather than mutated: Vue 2 doesn't track keys
  // added to or deleted from an existing object.
  const toggleRow = (row) => {
    const id = idOf(row)
    const next = { ...selected.value }
    if (next[id]) delete next[id]
    else next[id] = metaOf(row)
    selected.value = next
  }

  const clear = () => {
    generation += 1
    selected.value = {}
    selectingAll.value = false
  }

  const selectAll = async () => {
    const started = ++generation
    selectingAll.value = true
    try {
      const rows = await fetchAllMatching()
      if (started !== generation) return
      const next = {}
      rows.forEach((row) => { next[idOf(row)] = metaOf(row) })
      selected.value = next
    } finally {
      if (started === generation) selectingAll.value = false
    }
  }

  // Header checkbox: checked or partly checked -> deselect everything;
  // unchecked -> select every matching row on every page.
  const toggleHeader = () => (selectedCount.value ? clear() : selectAll())

  // Re-reads the given rows' meta after an action changed them in place
  // (e.g. Received), without dropping them from the selection.
  const refreshMeta = (rows) => {
    const next = { ...selected.value }
    rows.forEach((row) => {
      const id = idOf(row)
      if (next[id]) next[id] = metaOf(row)
    })
    selected.value = next
  }

  return {
    selected,
    total,
    selectingAll,
    selectedCount,
    selectedIds,
    selectedRows,
    allSelected,
    headerState,
    isSelected,
    toggleRow,
    toggleHeader,
    selectAll,
    clear,
    refreshMeta,
  }
}

// Stable string for a table's search + per-column filters, so an
// itemsProvider can tell a search/filter change (selection must clear)
// from a page or sort change (selection must stay).
export const criteriaSignature = (search, filters) =>
  JSON.stringify({ search: search || '', filters: (filters || []).map((f) => [f.key, f.value]) })
